import { FC, useEffect, useId, useState } from "react"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { LabelValue } from "@/types/common"
import { CheckedState } from "@radix-ui/react-checkbox"

export type CheckboxGroupProps = {
  id?: string
  className?: string
  label?: string
  hideLabel?: boolean
  options: LabelValue[]
  defaultValue?: string[]
  value?: string[]
  direction?: "row" | "col"
  disabled?: boolean
  disableDefaultSelected?: boolean
  truncate?: boolean
  onChange?: (value: string[]) => void
}

const CheckboxGroup: FC<CheckboxGroupProps> = ({
  id,
  className,
  label,
  options,
  defaultValue,
  value,
  direction = "row",
  disabled,
  hideLabel,
  disableDefaultSelected,
  truncate,
  onChange,
}) => {
  const defaultId = useId()
  const [localDefaultValue, setDefaultLocalValue] = useState<Set<string>>(
    new Set([])
  )
  const [localValue, setLocalValue] = useState<Set<string>>(new Set([]))

  useSyncValue()

  function handleChange(checked: CheckedState, option: LabelValue) {
    const newValue = new Set<string>(localValue)

    if (checked === "indeterminate" || !checked) {
      newValue.delete(option.value)
    } else {
      newValue.add(option.value)
    }
    setLocalValue(newValue)

    if (onChange) {
      onChange([...newValue.values()])
    }
  }

  function useSyncValue() {
    useEffect(() => {
      const newValue = new Set(value)
      setLocalValue(newValue)
    }, [value])

    useEffect(() => {
      const newDefaultValue = new Set(defaultValue)
      setDefaultLocalValue(newDefaultValue)
    }, [defaultValue])
  }

  function isDisabled(option: LabelValue): boolean {
    if (disabled || option.disabled) {
      return true
    }

    if (disableDefaultSelected && localDefaultValue.has(option.id)) {
      return true
    }

    return false
  }

  return (
    <div className={cn(className)}>
      {label && (
        <Label
          className={cn(
            "text-label-text mb-3 text-nowrap font-medium",
            hideLabel && "sr-only"
          )}
          htmlFor={id || defaultId}
        >
          {label}
        </Label>
      )}

      <div
        id={id || defaultId}
        className={cn([
          "flex flex-wrap gap-x-8 gap-y-4",
          direction === "col" && "flex-col",
        ])}
        aria-label={label}
      >
        {options.map((option) => (
          <div
            key={option.value}
            className="flex items-center gap-3 hover:cursor-pointer"
          >
            <Checkbox
              id={option.value}
              value={option.value}
              checked={localValue.has(option.value)}
              disabled={isDisabled(option)}
              onCheckedChange={(checked) => handleChange(checked, option)}
            />
            <Label
              htmlFor={option.value}
              className={cn(
                `
                  text-button-black block max-w-70
                  text-sm leading-6
                  peer-aria-checked:font-medium
                `,
                truncate && "truncate"
              )}
            >
              {option.label}
            </Label>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CheckboxGroup
